import { CSSProperties } from 'react';
import { InsightEvent } from '../../types/insights';
import { CATEGORY_STYLE } from './EventMarkerRow';

// ---------------------------------------------------------------------------
// EventDetailDrawer — right-side fixed panel for a selected event marker.
// Shows local + UTC timestamps, category/severity, PC + headset identity,
// deauth evidence (reason / match offset / confidence for detector events),
// run/experience/scene context, the note, and the log-bundle link.
// "Zoom ±5 min" re-windows the timeline around the event.
// ---------------------------------------------------------------------------

interface EventDetailDrawerProps {
  event: InsightEvent;
  pcName?: string | null;
  onZoom: (from: number, to: number) => void;
  onClose: () => void;
}

const rowLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: '#8a97a6',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 2,
};

const rowValue: CSSProperties = { fontSize: 13, color: '#16202e', wordBreak: 'break-word' };

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={rowLabel}>{label}</div>
      <div style={rowValue}>{children}</div>
    </div>
  );
}

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : typeof v === 'number' ? String(v) : null;

export default function EventDetailDrawer({ event, pcName, onZoom, onClose }: EventDetailDrawerProps) {
  const style = CATEGORY_STYLE[event.category];
  const d = new Date(event.ts * 1000);
  const p = event.payload;

  const hmdLabel = str(p['hmd_label']);
  const deauthReason =
    str(p['deauth_reason']) ?? str(p['reason']) ?? str(p['reason_code']);
  const experience = str(p['experience_name']);
  const version = str(p['experience_version']);
  const sceneName = str(p['scene_name']);
  const sceneIndex = str(p['scene_index']);
  const confidence = str(p['confidence']);
  const matchOffset = str(p['match_offset_s']);
  const matchMethod = str(p['match_method']);
  const durationS = str(p['duration_s']);
  const categoryRefinement = str(p['category']); // Mark Issue metadata.category

  const isDetector = event.src === 'detector';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 360,
        maxWidth: '92vw',
        background: '#fff',
        borderLeft: '1px solid #e4e9f0',
        boxShadow: '-10px 0 32px rgba(16,32,46,0.18)',
        zIndex: 950,
        overflowY: 'auto',
        padding: '18px 20px 28px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: `${style.color}1a`,
            color: style.color,
            fontSize: 12,
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: 100,
          }}
        >
          <span>{style.glyph}</span>
          {style.label}
          {categoryRefinement && event.category === 'operator_issue' ? ` · ${categoryRefinement}` : ''}
        </span>
        <button className="refresh-btn" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      <div style={{ fontSize: 15, fontWeight: 650, marginBottom: 14, color: '#16202e' }}>{event.title}</div>

      <Row label="Time (local)">
        <span style={{ fontVariant: 'tabular-nums' }}>{d.toLocaleString()}</span>
      </Row>
      <Row label="Time (UTC)">
        <span style={{ fontVariant: 'tabular-nums' }}>{d.toISOString().replace('T', ' ').replace('.000Z', 'Z')}</span>
      </Row>

      {event.endTs != null && (
        <Row label="Duration">
          {Math.round(event.endTs - event.ts)} s
          {durationS ? ` (reported ${durationS} s)` : ''}
        </Row>
      )}
      {event.endTs == null && durationS && <Row label="Duration">{durationS} s</Row>}

      <Row label="Severity">{event.severity}</Row>

      {(pcName || event.pcInstanceId) && (
        <Row label="PC">{pcName ?? event.pcInstanceId}</Row>
      )}

      {(hmdLabel || event.hmdMac) && (
        <Row label="Headset">
          {hmdLabel ? `${hmdLabel} · ` : ''}
          <span style={{ fontVariant: 'tabular-nums' }}>{event.hmdMac ?? '—'}</span>
        </Row>
      )}

      {deauthReason && <Row label="Deauth reason">{deauthReason}</Row>}

      {isDetector && (confidence || matchOffset) && (
        <Row label="Detector match">
          {confidence ?? 'unconfirmed'}
          {matchOffset ? ` · offset ${matchOffset} s` : ''}
          {matchMethod ? ` · ${matchMethod}` : ''}
        </Row>
      )}

      {(experience || sceneName || sceneIndex) && (
        <Row label="Run context">
          {experience ?? '—'}
          {version ? ` v${version}` : ''}
          {sceneName || sceneIndex ? ` · ${sceneName ?? `scene ${sceneIndex}`}` : ''}
        </Row>
      )}

      {event.runId && (
        <Row label="Run ID">
          <span style={{ fontVariant: 'tabular-nums', fontSize: 11 }}>{event.runId}</span>
        </Row>
      )}

      {event.detail && <Row label="Note">{event.detail}</Row>}

      {event.logUrl && (
        <Row label="Log bundle">
          <a href={event.logUrl} target="_blank" rel="noreferrer" style={{ color: '#2B6AC0' }}>
            Open log bundle
          </a>
        </Row>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button
          className="refresh-btn"
          style={{ background: '#2B6AC0', color: '#fff', fontWeight: 600 }}
          onClick={() => onZoom(event.ts - 300, (event.endTs ?? event.ts) + 300)}
        >
          Zoom ±5 min
        </button>
        <button className="refresh-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
